import { DownloadSimple, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, Loading, PageHeader } from "@/components/common";

export default function Invoices() {
  const { data, loading } = useGet("/client/invoices");
  if (loading) return <Loading label="Loading invoices" />;
  const invoices = data?.invoices || [];
  return (
    <div>
      <PageHeader
        eyebrow="Event administration"
        title="Invoices"
        description={
          "Review invoices shared by Gledswood. Payments are managed directly " +
          "with the venue."
        }
      />
      <section
        className="grid grid-cols-2 border-y border-border py-5"
        data-testid="invoice-summary"
      >
        <Summary label="Paid" value={data?.total_paid || 0} />
        <Summary label="Outstanding" value={data?.total_outstanding || 0} />
      </section>
      {invoices.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No invoices shared"
          description="Invoices shared by Gledswood will appear here."
        />
      ) : (
        <div className="mt-6 divide-y divide-border border-y border-border">
          {invoices.map((invoice) => (
            <InvoiceRow key={invoice.id} invoice={invoice} />
          ))}
        </div>
      )}
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div>
      <p className="eyebrow">{label}</p>
      <p className="font-heading text-3xl text-primary mt-1">
        {currency(value)}
      </p>
    </div>
  );
}
function InvoiceRow({ invoice }) {
  const download = async () => {
    try {
      const response = await api.get(`/client/invoices/${invoice.id}/file`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = invoice.original_filename || invoice.title;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  return (
    <article
      className="flex flex-wrap items-center justify-between gap-4 py-5"
      data-testid={`client-invoice-${invoice.id}`}
    >
      <div>
        <p className="font-heading text-2xl text-primary">{invoice.title}</p>
        <p className="text-sm text-muted-foreground mt-1">
          Due {formatDate(invoice.due_date)} · {currency(invoice.amount)}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Badge variant="outline" className="rounded-full capitalize">
          {invoice.status}
        </Badge>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={download}
          data-testid={`download-client-invoice-${invoice.id}`}
        >
          <DownloadSimple size={16} className="mr-1" /> Download
        </Button>
      </div>
    </article>
  );
}
function currency(value) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(Number(value || 0));
}
function formatDate(value) {
  return value
    ? new Date(`${value}T12:00:00`).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "date to be confirmed";
}
